import React, { useState, useEffect } from 'react';
import { Button } from '../base/Button';

export const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: 'Home', href: '#hero' },
    { name: 'Services', href: '#services' },
    { name: 'Portfolio', href: '#portfolio' },
    { name: 'About', href: '#about' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'Contact', href: '#contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
    }`}>
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="flex items-center gap-3">
              {/* Logo Icon - Paint Roller & Brush */}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                isScrolled ? 'bg-teal-500' : 'bg-white'
              }`}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Paint Roller */}
                  <rect x="10" y="4" width="10" height="6" rx="1" fill={isScrolled ? 'white' : '#14B8A6'} />
                  <rect x="13" y="10" width="4" height="8" rx="0.5" fill={isScrolled ? 'white' : '#0D9488'} />
                  <rect x="12" y="18" width="6" height="3" rx="1" fill={isScrolled ? 'white' : '#14B8A6'} />
                  
                  {/* Paint Brush */}
                  <path d="M4 20L7 17L9 19L6 22C5.5 22.5 4.5 22.5 4 22C3.5 21.5 3.5 20.5 4 20Z" fill={isScrolled ? 'white' : '#0D9488'} />
                  <rect x="7" y="15" width="3" height="4" rx="0.5" transform="rotate(-45 7 15)" fill={isScrolled ? 'white' : '#14B8A6'} />
                  
                  {/* Paint Drops */}
                  <circle cx="22" cy="8" r="1.5" fill={isScrolled ? 'white' : '#0D9488'} opacity="0.8" />
                  <circle cx="24" cy="12" r="1.2" fill={isScrolled ? 'white' : '#14B8A6'} opacity="0.6" />
                </svg>
              </div>
              
              {/* Logo Text */}
              <span className={`text-2xl font-bold font-inter transition-colors duration-300 ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>
                EB <span className="text-teal-500">Bright</span>
              </span>
            </h1>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('services')}
              className={`font-medium transition-colors duration-300 hover:text-teal-500 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`font-medium transition-colors duration-300 hover:text-teal-500 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className={`font-medium transition-colors duration-300 hover:text-teal-500 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              Reviews
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`font-medium transition-colors duration-300 hover:text-teal-500 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              Contact
            </button>
            <Button
              onClick={() => scrollToSection('quote')}
              variant="primary"
              size="sm"
            >
              Free Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 transition-colors duration-300 ${
              isScrolled ? 'text-gray-700' : 'text-white'
            }`}
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 py-4 border-t border-gray-200 bg-white rounded-lg shadow-lg">
            <div className="flex flex-col space-y-4 px-4">
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-700 font-medium hover:text-teal-500 text-left"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-gray-700 font-medium hover:text-teal-500 text-left"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className="text-gray-700 font-medium hover:text-teal-500 text-left"
              >
                Reviews
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-gray-700 font-medium hover:text-teal-500 text-left"
              >
                Contact
              </button>
              <Button
                onClick={() => scrollToSection('quote')}
                variant="primary"
                size="sm"
                className="w-full"
              >
                Free Quote
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
